import { Alert, Collapse as AntCollapse, Row, Spin, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { formatCurrency } from "../../../../libs/utils";
import { useTotalOverpaymentAmount } from "../../../Contracts/PostDatedCheques/ModifyPaymentStatus/hook/useTotalOverpaymentAmount";
import { getMoveOutDetailService } from "../../../TerminationProcess/ducks/services";
import { calculatePaidAmountAndDueDate, finalMoveOutSettlemet } from "../utils";

const { Text, Title } = Typography;

const PANEL_KEYS = ["A", "B", "C"];

type BreakdownRowProps = {
  label: string;
  value: string;
  total?: boolean;
};

type BreakdownPanelConfig = {
  panelKey: string;
  header: string;
  rows: BreakdownRowProps[];
};

const toNumber = (value: unknown): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return Number(value.replace(/,/g, "")) || 0;
  const parsedValue = Number(value || 0);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const BreakdownRow = (props: BreakdownRowProps): React.ReactElement => {
  const { label, value, total = false } = props;

  return (
    <Row
      justify="space-between"
      align="middle"
      style={{
        padding: total ? "12px" : "8px 12px",
        borderBottom: "none",
        background: total ? "var(--gray-100)" : "transparent",
        columnGap: "16px",
        flexWrap: "nowrap",
      }}
    >
      <Text
        style={{
          color: total ? "#212529" : "#78889a",
          fontWeight: total ? 700 : 400,
          lineHeight: "24px",
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          color: "#212529",
          fontWeight: 700,
          lineHeight: "24px",
          textAlign: "right",
          marginLeft: "auto",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Text>
    </Row>
  );
};

const createBreakdownPanel = (config: BreakdownPanelConfig) => {
  const { panelKey, header, rows } = config;

  return (
    <AntCollapse.Panel
      key={panelKey}
      header={<Text style={{ color: "#212529", fontWeight: 700, lineHeight: "24px" }}>{header}</Text>}
      style={{
        background: "transparent",
        border: "1px solid #dde2e8",
        borderRadius: 0,
        marginBottom: "16px",
        overflow: "hidden",
      }}
    >
      <div style={{ margin: "-16px" }}>
        {rows.map((row, index) => (
          <div
            key={row.label}
            style={{
              borderBottom: row.total || index === rows.length - 1 ? "none" : "1px solid #edf0f3",
            }}
          >
            <BreakdownRow label={row.label} value={row.value} total={row.total} />
          </div>
        ))}
      </div>
    </AntCollapse.Panel>
  );
};

const MoveOutBreakdown = () => {
  const { leaseId = "" } = useParams();
  const bookingDetails = useSelector((state: any) => state.newBooking?.bookingDetails);
  const vacatingDate = bookingDetails?.vacatingDate?.slice?.(0, 10);

  const [moveOutDetail, setMoveOutDetail] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [error, setError] = useState("");

  const { totalOverpaymentRemaining = 0 } = useTotalOverpaymentAmount(bookingDetails?.tenantId);

  useEffect(() => {
    let mounted = true;

    const getMoveOutDetail = async () => {
      if (!leaseId || !vacatingDate) {
        setError("Move-out date is unavailable for breakdown.");
        return;
      }

      setLoadingBreakdown(true);
      setError("");

      try {
        const response = await getMoveOutDetailService(leaseId, vacatingDate);

        if (!mounted) return;

        if (response) setMoveOutDetail(response);
        else setError("Unable to load move-out breakdown.");
      } catch (err) {
        if (mounted) setError("Unable to load move-out breakdown.");
      } finally {
        if (mounted) setLoadingBreakdown(false);
      }
    };

    getMoveOutDetail();

    return () => {
      mounted = false;
    };
  }, [leaseId, vacatingDate]);
  const { totalPaidAmount, securityDepositAmount } = useMemo(
    () => calculatePaidAmountAndDueDate(moveOutDetail?.paymentSchedule),
    [moveOutDetail?.paymentSchedule]
  );

  const settlementAmount = useMemo(
    () =>
      Number(
        finalMoveOutSettlemet(
          moveOutDetail,
          securityDepositAmount,
          totalPaidAmount,
          toNumber(totalOverpaymentRemaining)
        ) || 0
      ),
    [moveOutDetail, securityDepositAmount, totalPaidAmount, totalOverpaymentRemaining]
  );

  const daysOccupied = moveOutDetail?.daysOccupied || 0;
  const actualRentDue = toNumber(moveOutDetail?.actualRentDue);
  const rentAdjustment = actualRentDue - toNumber(totalPaidAmount);
  const terminationTotal = toNumber(moveOutDetail?.termination?.totalterminationPaneltyAmount);
  const maintenanceFee = toNumber(moveOutDetail?.settlementSummary?.maintenancefee);
  const otherAdminFee = toNumber(moveOutDetail?.settlementSummary?.otheradminfee);
  const deduction = toNumber(moveOutDetail?.settlementSummary?.deduction);
  const netAdjustments = terminationTotal + maintenanceFee + otherAdminFee - deduction - totalOverpaymentRemaining;
  const settlementAmountIsNegative = moveOutDetail?.settlementSummary?.settlementAmountIsNegative;
  const payableLabel =
    settlementAmountIsNegative === "yes"
      ? "Payable to Tenant"
      : settlementAmountIsNegative === "no"
        ? "Payable by Tenant"
        : "";

  const collapsePanels = [
    createBreakdownPanel({
      panelKey: "A",
      header: "A. Rent Reconciliation",
      rows: [
        {
          label: "Total Contract Value",
          value: formatCurrency(moveOutDetail?.leaseTerms?.contractAmount),
        },
        {
          label: "Rent Paid to Date",
          value: formatCurrency(-toNumber(totalPaidAmount)),
        },
        {
          label: `Actual Rent Due (${daysOccupied} Days)`,
          value: formatCurrency(actualRentDue),
        },
        {
          label: "Net Rent Adjustment",
          value: formatCurrency(rentAdjustment),
          total: true,
        },
      ],
    }),
    createBreakdownPanel({
      panelKey: "B",
      header: "B. Security Deposit Breakdown",
      rows: [
        {
          label: "Security Deposit Held",
          value: formatCurrency(securityDepositAmount),
        },
        {
          label: "Net Deposit Balance",
          value: formatCurrency(-toNumber(securityDepositAmount)),
          total: true,
        },
      ],
    }),
    createBreakdownPanel({
      panelKey: "C",
      header: "C. Termination & Adjustments",
      rows: [
        {
          label: "Termination Charge",
          value: formatCurrency(terminationTotal),
        },
        {
          label: "Maintenance Fee",
          value: formatCurrency(maintenanceFee),
        },
        {
          label: "Waiver",
          value: formatCurrency(-deduction),
        },
        {
          label: "Other Unpaid Dues",
          value: formatCurrency(otherAdminFee),
        },
        {
          label: "Overpayment Credit",
          value: formatCurrency(-totalOverpaymentRemaining),
        },
        {
          label: "Net Adjustments",
          value: formatCurrency(netAdjustments),
          total: true,
        },
      ],
    }),
  ];

  if (error) {
    return <Alert type="info" message={error} showIcon />;
  }

  if (loadingBreakdown || !moveOutDetail) {
    return (
      <div style={{ minHeight: "320px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <AntCollapse
        className="move-out-breakdown-collapse"
        ghost
        defaultActiveKey={PANEL_KEYS}
        expandIconPosition="right"
        style={{
          background: "transparent",
          border: "none",
        }}
      >
        {collapsePanels}
      </AntCollapse>

      <div
        style={{
          marginTop: "14px",
          padding: "18px 20px",
          border: "1px solid #c9e1ea",
          background: "#a8d7e6",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          flexWrap: "wrap",
          borderRadius: 0,
        }}
      >
        <div>
          <Text
            style={{
              display: "block",
              color: "#4a43e7",
              fontSize: "14px",
              fontWeight: 700,
              lineHeight: "20px",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            NET SETTLEMENT POSITION
          </Text>
          <Title
            level={2}
            style={{
              margin: "14px 0 0",
              color: "#3f4ad8",
              fontWeight: 700,
              fontSize: "30px",
              lineHeight: "38px",
            }}
          >
            {formatCurrency(Math.abs(toNumber(settlementAmount)))}
          </Title>
        </div>
        {payableLabel ? (
          <div
            style={{
              marginTop: "2px",
              padding: "8px 14px",
              fontSize: "14px",
              fontWeight: 700,
              borderRadius: 0,
              lineHeight: "20px",
              background: "#11a652",
              border: "1px solid #11a652",
              color: "#ffffff",
            }}
          >
            {payableLabel}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MoveOutBreakdown;
